import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Button, Checkbox, Drawer, Input, Modal, Tag, Tooltip, Typography } from '@arco-design/web-react';
import {
  IconApps,
  IconCalendar,
  IconDashboard,
  IconFile,
  IconSettings,
  IconThunderbolt,
} from '@arco-design/web-react/icon';
import type {
  PortalOperation,
  PortalOperationList,
  PortalNotificationPreview,
  PortalQuickTask,
} from '../types/portal.types';
import type { PortalApplication, PortalIdentity } from '../../../model/portalModels';
import { errorMessage } from '../../../common/api/httpClient';
import { usePortalOverlay } from '../../../components/PortalOverlayProvider';
import { buildMyTasksRoute } from '../../my-tasks/runtime/myTasksRoute';
import { pageIdentity, type PortalPage, type PortalPageIdentity } from '../../pages/api/pagesApi';
import PageFavoritesConfig from '../../pages/components/PageFavoritesConfig';
import SortableSelectedItems from '../../../components/SortableSelectedItems';
import { PortalOperationHost } from './PortalOperationHost';

const { Text } = Typography;

type WorkbenchOperationsProps = {
  applications: PortalApplication[];
  identity: PortalIdentity;
  onSessionExpired: () => void;
  operationLists: PortalOperationList[];
  initialFavoriteIds: string[];
  favoritePages: PortalPage[];
  pageFavoriteError?: string;
  onReloadPageFavorites: () => Promise<void>;
  quickTasks: PortalQuickTask[];
  notificationPreviews: PortalNotificationPreview[];
  onSaveFavorites: (operationIds: string[]) => Promise<void>;
  onSavePageFavorites: (pages: PortalPageIdentity[]) => Promise<void>;
  onApproveQuickTask: (task: PortalQuickTask) => Promise<void>;
  onNavigate: (path: string) => void;
  onOpenOperationList: () => void;
};

export function operationIcon(operation: PortalOperation): ReactNode {
  if (operation.icon === 'project') return <IconDashboard />;
  if (operation.icon === 'plan') return <IconCalendar />;
  if (operation.icon === 'material') return <IconApps />;
  if (operation.icon === 'monitor') return <IconThunderbolt />;
  if (operation.icon === 'report') return <IconFile />;
  return <IconSettings />;
}

export function WorkbenchOperationCard(props: {
  operation: PortalOperation;
  meta?: string;
  compact?: boolean;
  onOpen: (operation: PortalOperation) => void;
}) {
  const { operation } = props;
  return (
    <button
      className={[
        'reference-object-card',
        'portal-operation-card',
        props.compact ? 'portal-operation-card--compact' : '',
        !operation.enabled ? 'portal-operation-card--disabled' : ''
      ].filter(Boolean).join(' ')}
      type="button"
      onClick={() => props.onOpen(operation)}
    >
      <span className="portal-operation-card__icon">{operationIcon(operation)}</span>
      <span className="portal-operation-card__body">
        <strong>{operation.name}</strong>
        <span>{props.meta ?? `${operation.systemName} · ${operation.description}`}</span>
      </span>
      {!operation.enabled ? <Tag color="gray">暂不可用</Tag> : null}
    </button>
  );
}

function WorkbenchOperations(props: WorkbenchOperationsProps) {
  const { message } = usePortalOverlay();
  const allOperations = useMemo(
    () => props.operationLists.flatMap((list) => list.operations),
    [props.operationLists]
  );
  const operationMap = useMemo(
    () => new Map(allOperations.map((operation) => [operation.operationId, operation])),
    [allOperations]
  );
  const [favoriteIds, setFavoriteIds] = useState(props.initialFavoriteIds);
  const [draftFavoriteIds, setDraftFavoriteIds] = useState(props.initialFavoriteIds);
  const [configVisible, setConfigVisible] = useState(false);
  const [configListId, setConfigListId] = useState('all');
  const [configKeyword, setConfigKeyword] = useState('');
  const [pendingQuickTask, setPendingQuickTask] = useState<PortalQuickTask | null>(null);
  const [approvingQuickTask, setApprovingQuickTask] = useState(false);
  const [savingFavorites, setSavingFavorites] = useState(false);
  const [pageConfigVisible, setPageConfigVisible] = useState(false);
  const [activeOperation, setActiveOperation] = useState<PortalOperation | null>(null);

  useEffect(() => {
    setFavoriteIds(props.initialFavoriteIds);
    if (!configVisible) setDraftFavoriteIds(props.initialFavoriteIds);
  }, [configVisible, props.initialFavoriteIds]);

  const favoriteOperations = favoriteIds
    .map((operationId) => operationMap.get(operationId))
    .filter((operation): operation is PortalOperation => Boolean(operation));
  const visibleQuickTasks = props.quickTasks;

  const openOperation = (operation: PortalOperation) => {
    if (!operation.enabled) {
      message.warning(operation.unavailableReason ?? '当前操作暂不可用');
      return;
    }
    setActiveOperation(operation);
  };

  const openConfig = () => {
    setDraftFavoriteIds(favoriteIds);
    setConfigVisible(true);
  };

  const toggleFavorite = (operationId: string) => {
    setDraftFavoriteIds((current) => (
      current.includes(operationId)
        ? current.filter((id) => id !== operationId)
        : [...current, operationId]
    ));
  };

  const configOperations = allOperations.filter((operation) => {
    const listMatch = configListId === 'all' || operation.listId === configListId;
    const keyword = configKeyword.trim().toLowerCase();
    const keywordMatch = !keyword || `${operation.name}${operation.description}${operation.systemName}`.toLowerCase().includes(keyword);
    return listMatch && keywordMatch;
  });

  return (
    <>
      <div className="portal-workbench-panels">
        <section className="portal-workbench-panel portal-quick-tasks">
          <div className="portal-work-tools__section-head">
            <div><strong>快捷审批</strong><Text type="secondary">可直接处理的轻量事项</Text></div>
            <Tag color="arcoblue">{visibleQuickTasks.length} 项</Tag>
          </div>
          <div className="portal-quick-task-list">
            {visibleQuickTasks.map((task) => (
              <article className="portal-quick-task" key={task.taskId}>
                <button className="portal-quick-task__open" type="button"
                  onClick={() => props.onNavigate(buildMyTasksRoute('todo', task.targetTaskId))}>
                  <span className="portal-quick-task__head">
                    <strong>{task.title}</strong>
                    <Tag color="blue">{task.actionLabel}</Tag>
                  </span>
                  {task.summaryItems?.length ? (
                    <span className="portal-quick-task__fields">
                      {task.summaryItems.slice(0, 3).map(field => (
                        <span className="portal-quick-task__field" key={`${task.taskId}-${field.label}`}>
                          <span>{field.label}：</span><span>{field.value}</span>
                        </span>
                      ))}
                    </span>
                  ) : null}
                </button>
                <div className="portal-quick-task__footer">
                  <span className="portal-quick-task__time">{task.receivedAt}</span>
                  <Button size="small" type="primary" onClick={() => setPendingQuickTask(task)}>{task.actionLabel}</Button>
                </div>
              </article>
            ))}
            {visibleQuickTasks.length === 0 ? (
              <div className="portal-quick-task-list__empty"><strong>快捷任务已处理完毕</strong><Text type="secondary">需要详细审查的任务请进入“我的任务”。</Text></div>
            ) : null}
          </div>
        </section>

        <section className="portal-workbench-panel portal-work-tools__favorites">
          <div className="portal-work-tools__section-head">
            <div><strong>常用操作</strong><Text type="secondary">{favoriteOperations.length} 项</Text></div>
            <div className="portal-work-tools__actions">
              <Tooltip content="配置常用操作">
                <Button className="portal-work-tools__icon-button" icon={<IconSettings />} aria-label="配置常用操作" onClick={openConfig} />
              </Tooltip>
              <Tooltip content="打开操作列表">
                <Button className="portal-work-tools__icon-button" icon={<IconApps />} aria-label="打开操作列表" onClick={props.onOpenOperationList} />
              </Tooltip>
            </div>
          </div>
          <div className="portal-workbench-panel__scroll">
            {favoriteOperations.length > 0 ? (
              <div className="portal-operation-grid">
                {favoriteOperations.map((operation) => (
                  <WorkbenchOperationCard key={operation.operationId} operation={operation} compact onOpen={openOperation} />
                ))}
              </div>
            ) : (
              <div className="portal-work-tools__empty">
                <Text type="secondary">还没有常用操作</Text>
                <Button size="small" onClick={openConfig}>立即配置</Button>
              </div>
            )}
          </div>
        </section>

        <section className="portal-workbench-panel portal-common-interfaces">
          <div className="portal-work-tools__section-head">
            <div><strong>常用界面</strong><Text type="secondary">子系统页面快捷入口</Text></div>
            <div className="portal-work-tools__actions">
              <Tooltip content="配置常用界面">
                <Button className="portal-work-tools__icon-button" icon={<IconSettings />}
                  aria-label="配置常用界面" disabled={Boolean(props.pageFavoriteError)}
                  onClick={() => setPageConfigVisible(true)} />
              </Tooltip>
              <Tag color="arcoblue">{props.favoritePages.length} 项</Tag>
            </div>
          </div>
          <div className="portal-common-interfaces__list">
            {props.pageFavoriteError ? <div className="portal-work-tools__empty">
              <Text type="error">常用界面加载失败：{props.pageFavoriteError}</Text>
              <Button size="small" onClick={() => void props.onReloadPageFavorites()
                .catch(error => message.error(errorMessage(error)))}>重新加载</Button>
            </div> : null}
            {props.favoritePages.map(page => (
              <button className="portal-common-interface" key={pageIdentity(page)} type="button"
                onClick={() => props.onNavigate(page.routePath)}>
                <span className="portal-common-interface__icon" aria-hidden="true"><IconFile /></span>
                <span className="portal-common-interface__body">
                  <strong>{page.name}</strong>
                  <span>{page.applicationName}</span>
                </span>
                <span className="portal-common-interface__arrow" aria-hidden="true">›</span>
              </button>
            ))}
            {props.favoritePages.length === 0 && !props.pageFavoriteError ? (
              <div className="portal-work-tools__empty">
                <Text type="secondary">还没有常用界面</Text>
                <Button size="small" onClick={() => setPageConfigVisible(true)}>立即配置</Button>
              </div>
            ) : null}
          </div>
        </section>

        <section className="portal-workbench-panel portal-notification-preview">
          <div className="portal-work-tools__section-head">
            <div><strong>通知速览</strong><Text type="secondary">业务动态与系统通知，与抄送分离</Text></div>
            <Tag color="orange">{props.notificationPreviews.filter((notice) => notice.unread).length} 未读</Tag>
          </div>
          <div className="portal-notification-preview__viewport">
            <div className="portal-notification-preview__track">
              {props.notificationPreviews.length === 0 ? (
                <div className="portal-notification-preview__empty">
                  <strong>暂无通知</strong>
                  <Text type="secondary">当前暂无业务动态或系统通知。</Text>
                </div>
              ) : null}
              {props.notificationPreviews.map(notice => (
                  <button
                    className={notice.unread ? 'portal-system-notice portal-system-notice--unread' : 'portal-system-notice'}
                    key={notice.previewId}
                    type="button"
                    onClick={() => props.onNavigate(notice.routePath)}
                  >
                    <span className="portal-system-notice__head">
                      <strong>{notice.title}</strong>
                      <span className="portal-notification-preview__tags">
                        <Tag color={notice.kind === 'business' ? 'purple' : 'arcoblue'}>{notice.kind === 'business' ? '业务动态' : '系统通知'}</Tag>
                        {notice.level === 'important' ? <Tag color="red">重要</Tag> : null}
                      </span>
                    </span>
                    <span className="portal-system-notice__meta"><span>{notice.source}</span><span>{notice.publishedAt}</span></span>
                  </button>
              ))}
            </div>
          </div>
        </section>
      </div>

      <Modal
        title="确认快速通过"
        visible={Boolean(pendingQuickTask)}
        okText="确认通过"
        cancelText="取消"
        confirmLoading={approvingQuickTask}
        onCancel={() => setPendingQuickTask(null)}
        onOk={() => {
          if (!pendingQuickTask) return;
          setApprovingQuickTask(true);
          void props.onApproveQuickTask(pendingQuickTask)
            .then(() => {
              message.success(`“${pendingQuickTask.title}”已通过`);
              setPendingQuickTask(null);
            })
            .catch(error => message.error(errorMessage(error)))
            .finally(() => setApprovingQuickTask(false));
        }}
      >
        {pendingQuickTask ? (
          <div className="portal-quick-task-confirm">
            <p>确认直接{pendingQuickTask.actionLabel}“{pendingQuickTask.title}”吗？</p>
            <Text type="secondary">请确认摘要信息；{pendingQuickTask.actionLabel}后将从当前快捷列表移除。</Text>
          </div>
        ) : null}
      </Modal>

      <Drawer
        title="配置常用操作"
        visible={configVisible}
        width={720}
        className="portal-favorites-config-drawer"
        onCancel={() => setConfigVisible(false)}
        footer={(
          <div className="portal-common-config__footer">
            <Text type="secondary">已选择 {draftFavoriteIds.length} 项</Text>
            <div>
              <Button onClick={() => setConfigVisible(false)}>取消</Button>
              <Button
                type="primary"
                loading={savingFavorites}
                onClick={() => {
                  setSavingFavorites(true);
                  void props.onSaveFavorites(draftFavoriteIds)
                    .then(() => {
                      setFavoriteIds(draftFavoriteIds);
                      setConfigVisible(false);
                      message.success('常用操作已更新');
                    })
                    .catch(error => message.error(errorMessage(error)))
                    .finally(() => setSavingFavorites(false));
                }}
              >
                保存配置
              </Button>
            </div>
          </div>
        )}
      >
        <div className="portal-common-config portal-common-config--favorites">
          <Input.Search allowClear value={configKeyword} placeholder="搜索操作名称" onChange={setConfigKeyword} />
          <div className="portal-operation-list-tabs">
            <button className={configListId === 'all' ? 'active' : ''} type="button" onClick={() => setConfigListId('all')}>全部</button>
            {props.operationLists.map((list) => (
              <button className={configListId === list.listId ? 'active' : ''} key={list.listId} type="button" onClick={() => setConfigListId(list.listId)}>{list.name}</button>
            ))}
          </div>
          <div className="portal-common-config__body">
            <div className="portal-common-config__options portal-common-config__options--favorites">
              {configOperations.map((operation) => (
                <label className="portal-common-config__option" key={operation.operationId}>
                  <Checkbox checked={draftFavoriteIds.includes(operation.operationId)} onChange={() => toggleFavorite(operation.operationId)} />
                  <span className="portal-operation-card__icon">{operationIcon(operation)}</span>
                  <span>
                    <strong>{operation.name}</strong>
                    <small>{operation.systemName} · {props.operationLists.find((list) => list.listId === operation.listId)?.name}</small>
                  </span>
                </label>
              ))}
            </div>
            <div className="portal-common-config__selected portal-common-config__selected--favorites">
              <SortableSelectedItems
                items={draftFavoriteIds.flatMap(operationId => {
                  const operation = operationMap.get(operationId);
                  return operation ? [{ id: operationId, label: operation.name }] : [];
                })}
                onReorder={setDraftFavoriteIds} />
            </div>
          </div>
        </div>
      </Drawer>

      <Drawer
        className="portal-operation-drawer"
        title="快捷操作"
        width="min(1100px, calc(100vw - 88px))"
        visible={Boolean(activeOperation)}
        footer={null}
        onCancel={() => setActiveOperation(null)}
      >
        {activeOperation ? (
          <PortalOperationHost
            key={activeOperation.operationId}
            applicationCode={activeOperation.applicationCode}
            operationCode={activeOperation.operationCode}
            applications={props.applications}
            identity={props.identity}
            onSessionExpired={props.onSessionExpired}
            onClose={() => setActiveOperation(null)}
          />
        ) : null}
      </Drawer>

      {pageConfigVisible ? <PageFavoritesConfig
        favoritePages={props.favoritePages}
        onSave={props.onSavePageFavorites}
        onClose={() => setPageConfigVisible(false)} /> : null}
    </>
  );
}

export default WorkbenchOperations;
